// Phase T2.0A Prompt 2.2 — Registration CTA / banner + Alternate flow.
//
// States (registration_open):
//   1. isAlternate=true      → "You're on the Alternate List" + Leave link
//   2. isRegistered=true     → registered banner + Withdraw
//   3. isFull=true & !me     → "Event Full" + "Join Alternate List" CTA
//   4. otherwise             → "Register for Event"
//
// Alternate join/leave uses the alternatesAPI directly — parent passes
// the eventKind + eventId so this component stays generic across tournaments
// and round robins.

import React, { useState } from 'react';
import { CheckCircle, Users } from '@phosphor-icons/react';
import { toast } from 'sonner';
import WithdrawConfirmDialog from './WithdrawConfirmDialog';
import ConfirmDialog from './ConfirmDialog';
import { alternatesAPI } from '../../lib/alternatesApi';

export default function RegistrationCTA({
  status,
  isRegistered,
  isFull,
  isAlternate = false,
  alternatePosition = null,
  onOpenRegistration,
  onWithdraw,
  withdrawBusy = false,
  eventKind,     // 'tournament' | 'round_robin' | 'club_event'
  eventId,
  onReload,      // () => Promise<void> — re-fetch event after alt join/leave
  viewerPaid = false,   // enables the paid-safety warning in the Withdraw dialog
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLeaveAltOpen, setConfirmLeaveAltOpen] = useState(false);
  const [altBusy, setAltBusy] = useState(false);

  // registration_closed → static banner, no action
  if (status === 'registration_closed') {
    return (
      <div
        className="border border-amber-200 bg-amber-50 rounded-2xl px-4 py-3 text-center"
        data-testid="registration-closed-banner"
      >
        <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold">Registration Closed</div>
        <div className="text-[12.5px] text-amber-900 mt-0.5">This event is no longer accepting new registrations.</div>
      </div>
    );
  }

  if (status !== 'registration_open') return null;

  async function joinAlternate() {
    if (altBusy) return;
    setAltBusy(true);
    try {
      await alternatesAPI.join(eventKind, eventId);
      toast.success("You've been added to the alternate list.");
      await onReload?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not join alternate list.'));
    } finally {
      setAltBusy(false);
    }
  }

  async function leaveAlternate() {
    if (altBusy) return;
    setAltBusy(true);
    try {
      await alternatesAPI.leave(eventKind, eventId);
      toast.success('Removed from the alternate list.');
      await onReload?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not leave alternate list.'));
    } finally {
      setAltBusy(false);
    }
  }

  // 1) Alternate takes precedence over full/registered/register button.
  if (isAlternate) {
    return (
      <>
        <div
          className="flex items-center justify-between gap-3 border border-amber-200 bg-amber-50 rounded-2xl px-4 py-3"
          data-testid="alternate-banner"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Users size={18} weight="fill" className="text-amber-700 shrink-0" />
            <div className="min-w-0">
              <div className="text-[13px] text-amber-900 font-semibold">You&apos;re on the Alternate List</div>
              <div className="text-[11.5px] text-amber-800/90" data-testid="alternate-position">
                {alternatePosition ? `Alternate #${alternatePosition}` : 'You will be notified if a spot opens up.'}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setConfirmLeaveAltOpen(true)}
            disabled={altBusy}
            data-testid="cta-leave-alternate-btn"
            className="shrink-0 text-[12px] font-medium text-amber-900 hover:underline min-h-[40px] px-2 disabled:opacity-50"
          >
            {altBusy ? 'Leaving\u2026' : 'Leave Alternate List'}
          </button>
        </div>
        <ConfirmDialog
          open={confirmLeaveAltOpen}
          onOpenChange={setConfirmLeaveAltOpen}
          title="Leave alternate list?"
          description="You will lose your current position on the waitlist. If a spot opens up you'll no longer be notified."
          confirmLabel="Leave List"
          cancelLabel="Stay on List"
          destructive
          busy={altBusy}
          onConfirm={async () => {
            await leaveAlternate();
            setConfirmLeaveAltOpen(false);
          }}
        />
      </>
    );
  }

  // 2) Registered.
  if (isRegistered) {
    return (
      <>
        <div
          className="flex items-center justify-between gap-3 border border-emerald-200 bg-emerald-50 rounded-full px-4 py-2.5"
          data-testid="registered-banner"
        >
          <div className="flex items-center gap-2 min-w-0">
            <CheckCircle size={18} weight="fill" className="text-emerald-700 shrink-0" />
            <div className="text-[13px] text-emerald-900 font-semibold uppercase tracking-wider truncate">
              You are registered for this event
            </div>
          </div>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            data-testid="cta-withdraw-btn"
            className="shrink-0 text-[12px] font-medium text-[#8a1c1c] hover:underline min-h-[40px] px-2"
          >
            Withdraw
          </button>
        </div>
        <WithdrawConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          busy={withdrawBusy}
          viewerPaid={viewerPaid}
          onConfirm={async () => {
            await onWithdraw();
            setConfirmOpen(false);
          }}
        />
      </>
    );
  }

  // 3) Full → offer alternate join.
  if (isFull) {
    return (
      <div
        className="border border-stone-200 bg-stone-50 rounded-2xl px-4 py-4 text-center space-y-2"
        data-testid="event-full-banner"
      >
        <div className="text-[11px] uppercase tracking-wider text-stone-700 font-semibold">Event Full</div>
        <div className="text-[12.5px] text-stone-800">
          Registration has reached the maximum player limit.
        </div>
        <button
          type="button"
          onClick={joinAlternate}
          disabled={altBusy}
          data-testid="cta-join-alternate-btn"
          className="mt-1 w-full sm:w-auto sm:min-w-[220px] px-5 py-3 rounded-full bg-[#c9a93a] text-[#013523] text-[13px] font-semibold hover:bg-[#b6923c] transition-colors min-h-[48px] shadow-sm disabled:opacity-60"
        >
          {altBusy ? 'Joining…' : 'Join Alternate List'}
        </button>
      </div>
    );
  }

  // 4) Default → Register.
  return (
    <button
      type="button"
      onClick={onOpenRegistration}
      data-testid="cta-register-btn"
      className="w-full sm:w-auto sm:min-w-[240px] px-5 py-3 rounded-full bg-[#015e3f] text-white text-[14px] font-semibold hover:bg-[#013523] transition-colors min-h-[48px] shadow-sm"
    >
      Register for Event
    </button>
  );
}
